// src/routes.jsx
import { lazy, Suspense } from "react";
import { Outlet, Route, Routes } from "react-router-dom";
import SplashScreen from "./components/SplashScreen";
import { SavedJobsProvider } from "./context/SavedJobsContext";

// Layout route that mounts SavedJobsProvider once around all job-browsing
// pages that call useSavedJobs(). Keeping it scoped here (rather than around
// the whole app) means the provider's auth + saved_jobs lookup only runs when
// the user actually visits a jobs route, and the same provider instance is
// shared across /jobs ↔ /jobs/:id so saved-job state isn't refetched on every
// navigation.
function SavedJobsScope() {
  return (
    <SavedJobsProvider>
      <Outlet />
    </SavedJobsProvider>
  );
}

// Public / Auth
const HomePage = lazy(() => import("./pages/HomePage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SignUpPage = lazy(() => import("./pages/SignUpPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));

// Profile
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const PublicProfilePage = lazy(() => import("./pages/PublicProfilePage"));

// Job Seeker
const JobSeekerHomePage = lazy(() => import("./pages/JobSeekerHomePage"));
const JobSearchPage = lazy(() => import("./pages/JobSearchPage"));
const JobDetailsPage = lazy(() => import("./pages/JobDetailsPage"));
const ApplyJobPage = lazy(() => import("./pages/ApplyJobPage"));
const MyApplicationsPage = lazy(() => import("./pages/MyApplicationsPage"));

// Employer
const EmployerHomePage = lazy(() => import("./pages/EmployerHomePage"));
const EmployerDashboard = lazy(() => import("./pages/EmployerDashboard"));
const PostJobPage = lazy(() => import("./pages/PostJobPage"));
const EditJobPage = lazy(() => import("./pages/EditJobPage"));
const ApplicantsByJobPage = lazy(() => import("./pages/ApplicantsByJobPage"));

// Admin
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AllApplicationsPage = lazy(() => import("./pages/AllApplicationsPage"));
const AllJobsPage = lazy(() => import("./pages/AllJobsPage"));
const AdminFeedbackPage = lazy(() => import("./pages/AdminFeedbackPage"));

// Ads
const AdsPage = lazy(() => import("./pages/AdsPage"));
const PostAdPage = lazy(() => import("./pages/PostAdPage"));
const EditAdPage = lazy(() => import("./pages/EditAdPage"));
const AdDetailsPage = lazy(() => import("./pages/AdDetailsPage"));

// Messaging & Directory
const InboxPage = lazy(() => import("./pages/InboxPage"));
const SendMessagePage = lazy(() => import("./pages/SendMessagePage"));
const SearchUserPage = lazy(() => import("./pages/SearchUserPage"));
const UserDirectoryPage = lazy(() => import("./pages/UserDirectoryPage"));

// Consultants
const FindConsultantPage = lazy(() => import("./pages/FindConsultantPage"));
const ConsultantProfilePage = lazy(() => import("./pages/ConsultantProfilePage"));
const ConsultantFormPage = lazy(() => import("./pages/ConsultantFormPage"));
const ConsultantDashboard = lazy(() => import("./pages/ConsultantDashboard"));
const ConsultantHomePage = lazy(() => import("./pages/ConsultantHomePage"));
const ConsultantInbox = lazy(() => import("./pages/ConsultantInbox"));
const ViewClientRequestsPage = lazy(() => import("./pages/ViewClientRequestsPage"));
const ClientRequestsForm = lazy(() => import("./pages/ClientRequestsForm"));
const HireConsultantPage = lazy(() => import("./pages/HireConsultantPage"));

// Resume & Interviews
const ResumeBuilderPage = lazy(() => import("./pages/ResumeBuilderPage"));
const ViewResumePage = lazy(() => import("./pages/ViewResumePage"));
const ScheduleInterviewPage = lazy(() => import("./pages/ScheduleInterviewPage"));
const MyInterviewsPage = lazy(() => import("./pages/MyInterviewsPage"));

// Misc
const FeedbackPage = lazy(() => import("./pages/FeedbackPage"));
const Faq = lazy(() => import("./pages/Faq"));
const AboutUsPage = lazy(() => import("./pages/AboutUsPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));

export default function AppRoutes() {
  return (
    <Suspense fallback={<SplashScreen />}>
      <Routes>
        {/* Public / Auth */}
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignUpPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />

        {/* Profile */}
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/profile/:id" element={<ProfilePage />} />
        <Route path="/profile/:userId" element={<PublicProfilePage />} />

        {/* Job Seeker */}
        <Route path="/job-seeker/home" element={<JobSeekerHomePage />} />
        <Route element={<SavedJobsScope />}>
          <Route path="/jobs" element={<JobSearchPage />} />
          <Route path="/browse-jobs" element={<JobSearchPage />} />
          <Route path="/jobs/:id" element={<JobDetailsPage />} />
        </Route>
        <Route path="/jobs/:jobId/apply" element={<ApplyJobPage />} />
        <Route path="/applications" element={<MyApplicationsPage />} />

        {/* Employer */}
        <Route path="/employer/home" element={<EmployerHomePage />} />
        <Route path="/employer/dashboard" element={<EmployerDashboard />} />
        <Route path="/post-job" element={<PostJobPage />} />
        <Route path="/employer/jobs/:jobId/edit" element={<EditJobPage />} />
        <Route
          path="/employer/applicants/:jobId"
          element={<ApplicantsByJobPage />}
        />

        {/* Admin */}
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/applications" element={<AllApplicationsPage />} />
        <Route path="/admin/jobs" element={<AllJobsPage />} />
        <Route path="/admin-feedback" element={<AdminFeedbackPage />} />

        {/* Ads */}
        <Route path="/ads" element={<AdsPage />} />
        <Route path="/browse-ads" element={<AdsPage />} />
        <Route path="/post-ad" element={<PostAdPage />} />
        <Route path="/ads/:adId/edit" element={<EditAdPage />} />
        <Route path="/edit-ad/:adId" element={<EditAdPage />} />
        <Route path="/ads/:id" element={<AdDetailsPage />} />

        {/* Messaging & Directory */}
        <Route path="/inbox" element={<InboxPage />} />
        <Route path="/send-message" element={<SendMessagePage />} />
        <Route path="/search-users" element={<SearchUserPage />} />
        <Route path="/directory" element={<UserDirectoryPage />} />

        {/* Consultants */}
        <Route path="/consultants" element={<FindConsultantPage />} />
        <Route path="/consultant-profile" element={<ConsultantProfilePage />} />
        <Route path="/consultant-form" element={<ConsultantFormPage />} />
        <Route path="/consultant-dashboard" element={<ConsultantDashboard />} />
        <Route path="/consultant/home" element={<ConsultantHomePage />} />
        <Route path="/consultant/inbox" element={<ConsultantInbox />} />
        <Route
          path="/consultant/client-requests"
          element={<ViewClientRequestsPage />}
        />
        <Route path="/client-requests" element={<ClientRequestsForm />} />
        <Route path="/hire-consultant" element={<HireConsultantPage />} />

        {/* Resume & Interviews */}
        <Route path="/resume-builder" element={<ResumeBuilderPage />} />
        <Route path="/view-resume" element={<ViewResumePage />} />
        <Route path="/schedule-interview" element={<ScheduleInterviewPage />} />
        <Route path="/my-interviews" element={<MyInterviewsPage />} />

        {/* Misc */}
        <Route path="/feedback" element={<FeedbackPage />} />
        <Route path="/faq" element={<Faq />} />
        <Route path="/about" element={<AboutUsPage />} />
        <Route path="/terms" element={<TermsPage />} />
      </Routes>
    </Suspense>
  );
}
